import type { OrganizationUser } from '@/clients/v1';
import { computed, ref, type WritableComputedRef } from 'vue';

export interface LanguageSelect {
  label: string;
  value: string;
  country: string;
}

export function useLanguages() {
  const DEFAULT_LANGUAGE: LanguageSelect = {
    label: 'English',
    value: 'en',
    country: 'us',
  };

  const selectedLanguage = ref<LanguageSelect>(DEFAULT_LANGUAGE);

  const availableLanguages = computed((): LanguageSelect[] => {
    return [
      {
        label: 'English',
        value: 'en',
        country: 'us',
      },
      {
        label: 'Italian',
        value: 'it',
        country: 'it',
      },
      {
        label: 'German',
        value: 'de',
        country: 'de',
      },
      {
        label: 'French',
        value: 'fr',
        country: 'fr',
      },
      {
        label: 'Spanish',
        value: 'es',
        country: 'es',
      },
    ];
  });

  function setUILanguage(user: OrganizationUser, locale: WritableComputedRef<string, string>) {
    const preferredLanguageCode = user.language;

    const matchedLanguage = availableLanguages.value.find(
      (lang) => lang.value === preferredLanguageCode,
    );

    if (matchedLanguage) {
      const localeKey = `${matchedLanguage.value}-${matchedLanguage.country.toUpperCase()}`;
      locale.value = localeKey;
    } else {
      const fallbackLang = availableLanguages.value[0]; // en-US

      locale.value = `${fallbackLang.value}-${fallbackLang.country.toUpperCase()}`;
    }

    localStorage.setItem('selectedLanguage', locale.value);
  }

  function initializeLanguage(configLanguage: string | null) {
    if (configLanguage) {
      const matchedLanguage = availableLanguages.value.find(
        (lang) => lang.value === configLanguage,
      );
      selectedLanguage.value = matchedLanguage || availableLanguages.value[0];
    }
  }

  return {
    DEFAULT_LANGUAGE,
    selectedLanguage,
    availableLanguages,
    initializeLanguage,
    setUILanguage,
  };
}
